"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import AppWrapper from "@/shared/ui/components/app-wrapper";
import Button from "@/shared/ui/components/button";
import { type CheckoutSession } from "@/types";
import { useCosmosClient } from "@/services/cosmos";
import { withCheckoutId } from "@/lib/checkout-id";
import { isConflictError } from "@/lib/http-error-recovery";
import { logInfo, logError } from "@/services/logging/logger";
import { browserAgent } from "@/shared/telemetry/newrelic";
import { newLocalStorageStore } from "@/lib/store";
import { parseLocale } from "@/i18n";
import { syncCheckoutHandoffCookies } from "@/services/cookie/checkout-handoff";
import { type ExternalVerificationConfig } from "../types";
import * as externalVerificationLib from "../lib";
import { VerificationIcon } from "../components/verification-icon";

type ReturnViewState = "verifying" | "pending" | "error";

interface ExternalVerificationReturnPageProps {
  config: ExternalVerificationConfig;
  onCheckoutFetched: (session: CheckoutSession) => void;
}

export function ExternalVerificationReturnPage({
  config,
  onCheckoutFetched,
}: ExternalVerificationReturnPageProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const cosmosClient = useCosmosClient();
  const [viewState, setViewState] = useState<ReturnViewState>("verifying");
  const [pendingSession, setPendingSession] = useState<CheckoutSession | null>(
    null,
  );
  /** Trimmed redirect URL from the verify response — used for Resume. */
  const [pendingRedirectUrl, setPendingRedirectUrl] = useState("");
  const [verifying, setVerifying] = useState(true);
  const [resuming, setResuming] = useState(false);
  const [leavingToMerchant, setLeavingToMerchant] = useState(false);
  const hasVerifiedRef = useRef(false);

  const localePrefix = config.localeKey;
  const showFooter = viewState === "pending" || viewState === "error";

  const completeVerification = useCallback(
    async (session: CheckoutSession) => {
      hasVerifiedRef.current = true;

      const store = newLocalStorageStore(window, session.id);
      await externalVerificationLib.clearRedirectInitiated(store, config);

      const outcome = externalVerificationLib.getPostVerifyOutcome(
        session,
        config,
      );

      // Keep the SWR cache fresh either way. The return path is self-navigating,
      // so this never triggers the redirect handler to navigate away from here.
      onCheckoutFetched(session);

      if (outcome.type === "pending-resume") {
        // Allow later retry/resume: verification did not finish.
        hasVerifiedRef.current = false;
        logInfo(
          config.logTag,
          "External verification still pending with redirect URL; showing resume UI",
          { checkout_id: session.id, checkout_status: session.status },
        );
        browserAgent.addPageAction(config.telemetry.verifyFailed, {
          checkout_id: session.id,
          checkout_status: session.status,
          reason: "pending_resume",
        });
        setPendingSession(session);
        setPendingRedirectUrl(outcome.redirectUrl);
        setViewState("pending");
        return;
      }

      if (outcome.type === "await-incomplete") {
        // Allow retry: verification did not finish, so do not treat this as done.
        hasVerifiedRef.current = false;
        // Backend still reports this step as pending but there is no redirect
        // URL to resume into, so surface the hard-error retry/back state.
        logInfo(
          config.logTag,
          "External verification returned still-awaiting status without redirect URL",
          { checkout_id: session.id, checkout_status: session.status },
        );
        browserAgent.addPageAction(config.telemetry.verifyFailed, {
          checkout_id: session.id,
          checkout_status: session.status,
          reason: "await_incomplete_no_redirect",
        });
        setPendingSession(null);
        setPendingRedirectUrl("");
        setViewState("error");
        return;
      }

      browserAgent.addPageAction(config.telemetry.verifySuccess, {
        checkout_id: session.id,
        checkout_status: session.status,
      });

      navigate(withCheckoutId(outcome.route), { replace: true });
    },
    [onCheckoutFetched, navigate, config],
  );

  const verifyAndResume = useCallback(
    async (isRetry = false) => {
      if (hasVerifiedRef.current) {
        return;
      }

      const checkoutId = cosmosClient.config().checkoutId;
      const verifyKey = `${config.verifyEndpoint}:${checkoutId}`;

      if (isRetry) {
        externalVerificationLib.clearVerifyRequestDedup(verifyKey);
      }

      setVerifying(true);
      setViewState("verifying");
      setPendingSession(null);
      setPendingRedirectUrl("");

      logInfo(config.logTag, "Verifying external verification completion");

      browserAgent.addPageAction(config.telemetry.returnFromUi, {
        checkout_id: checkoutId,
      });

      try {
        const session = await externalVerificationLib.deduplicatedVerifyRequest(
          verifyKey,
          () => cosmosClient.verifyExternalStep(config.verifyEndpoint),
        );

        if (hasVerifiedRef.current) {
          return;
        }

        await completeVerification(session);
      } catch (err) {
        if (hasVerifiedRef.current) {
          return;
        }

        if (isConflictError(err)) {
          try {
            const session = await cosmosClient.getCheckoutSessionV3();
            logInfo(
              config.logTag,
              "Verify returned 409; applying post-verify outcome from refetched session",
              { checkoutId, status: session.status },
            );
            // May resume checkout, show pending-resume, or hard-error depending
            // on status + redirection URL — same branching as a 200 response.
            await completeVerification(session);
            return;
          } catch (refetchErr) {
            logError(
              config.logTag,
              "Failed to refetch session after verify conflict",
              refetchErr instanceof Error
                ? refetchErr
                : new Error(String(refetchErr)),
            );
          }
        }

        externalVerificationLib.clearVerifyRequestDedup(verifyKey);
        setPendingSession(null);
        setPendingRedirectUrl("");
        setViewState("error");
        logError(
          config.logTag,
          "Failed to verify external verification completion",
          err instanceof Error ? err : new Error(String(err)),
        );

        browserAgent.addPageAction(config.telemetry.verifyFailed, {
          checkout_id: checkoutId,
        });
      } finally {
        setVerifying(false);
      }
    },
    [cosmosClient, completeVerification, config],
  );

  useEffect(() => {
    if (hasVerifiedRef.current) {
      return;
    }
    verifyAndResume();
  }, [verifyAndResume]);

  const handleBackToCheckout = useCallback(() => {
    navigate(withCheckoutId("/"), { replace: true });
  }, [navigate]);

  const handleResumeExternalVerification = useCallback(async () => {
    if (!pendingSession || !pendingRedirectUrl || resuming) {
      return;
    }

    setResuming(true);

    const store = newLocalStorageStore(window, pendingSession.id);
    // Match first-time redirect: mark initiated so a bounce back through
    // `/kyc` or `/questionnaire` goes to the return handler, not a loop.
    await externalVerificationLib.setRedirectInitiated(store, config);

    const localizedRedirectUrl =
      externalVerificationLib.applyLocaleToExternalRedirectUrl(
        pendingRedirectUrl,
        i18n.language,
      );

    logInfo(config.logTag, "Resuming external verification UI", {
      checkout_id: pendingSession.id,
      redirectUrl: localizedRedirectUrl.substring(0, 50) + "...",
    });

    browserAgent.addPageAction(config.telemetry.redirectToUi, {
      checkout_id: pendingSession.id,
      reason: "pending_resume",
    });

    // Same handoff cookies as the first-time redirect page.
    syncCheckoutHandoffCookies({
      customerId: pendingSession.customerId,
      locale: parseLocale(i18n.language) ?? pendingSession.locale,
    });

    window.location.href = localizedRedirectUrl;
  }, [pendingSession, pendingRedirectUrl, resuming, config, i18n.language]);

  const handleBackToMerchant = useCallback(() => {
    if (!pendingSession || leavingToMerchant) {
      return;
    }

    const cancelUrl = pendingSession.merchant?.urls?.cancel?.trim();
    if (!cancelUrl) {
      logError(
        config.logTag,
        "No merchant cancel URL available for back-to-merchant",
        new Error("Missing merchant cancel URL"),
        { checkout_id: pendingSession.id },
      );
      navigate(withCheckoutId("/"), { replace: true });
      return;
    }

    setLeavingToMerchant(true);

    logInfo(config.logTag, "Redirecting to merchant cancel URL", {
      checkout_id: pendingSession.id,
    });

    window.location.href = cancelUrl;
  }, [pendingSession, leavingToMerchant, config, navigate]);

  return (
    <AppWrapper
      visible={true}
      showLogo
      showCartButton={false}
      showLanguageSwitcher
      headerBottomSpacing="none"
      stickyFooterWithShadow={showFooter}
      stickyFooter={
        showFooter
          ? () =>
              viewState === "pending" ? (
                <div className="flex flex-col gap-3">
                  <Button
                    size="large"
                    onClick={handleResumeExternalVerification}
                    loading={resuming}
                    disabled={leavingToMerchant}
                  >
                    {t(`${localePrefix}.resume`)}
                  </Button>
                  <Button
                    size="large"
                    variant="plain"
                    onClick={handleBackToMerchant}
                    loading={leavingToMerchant}
                    disabled={resuming}
                  >
                    {t(`${localePrefix}.back_to_merchant`)}
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  <Button
                    size="large"
                    onClick={() => verifyAndResume(true)}
                    loading={verifying}
                  >
                    {t(`${localePrefix}.retry`)}
                  </Button>
                  <Button
                    size="large"
                    variant="plain"
                    onClick={handleBackToCheckout}
                    disabled={verifying}
                  >
                    {t(`${localePrefix}.back_to_checkout`)}
                  </Button>
                </div>
              )
          : undefined
      }
    >
      {() => (
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 py-10">
          {viewState === "verifying" ? (
            <>
              <div className="relative mb-8 size-24 shrink-0">
                <div className="flex size-24 items-center justify-center rounded-full bg-gradient-to-br from-purple-100 to-purple-200 text-purple-600">
                  <VerificationIcon icon="check" />
                </div>
                <div
                  className="absolute -bottom-0.5 -right-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-white shadow-sm"
                  aria-hidden="true"
                >
                  <div className="size-5 animate-spin rounded-full border-2 border-purple-100 border-t-[#9600F1]" />
                </div>
              </div>

              <h1 className="text-2xl font-bold text-gray-900 text-center mb-3">
                {t(`${localePrefix}.return_title`)}
              </h1>

              <p className="text-gray-600 text-center text-lg mb-8 max-w-xs">
                {t(`${localePrefix}.verifying_message`)}
              </p>

              <div className="flex items-center gap-2 px-4 py-2 bg-purple-50 rounded-full">
                <svg
                  className="w-4 h-4 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="text-sm text-purple-700 font-medium">
                  {t(`${localePrefix}.verifying_hint`)}
                </span>
              </div>
            </>
          ) : viewState === "pending" ? (
            <>
              <div className="w-24 h-24 mb-8 rounded-full bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center text-purple-600">
                <VerificationIcon icon={config.redirectIcon} />
              </div>

              <h1 className="text-2xl font-bold text-gray-900 text-center mb-3">
                {t(`${localePrefix}.pending_title`)}
              </h1>

              <p className="text-gray-600 text-center text-lg max-w-xs">
                {t(`${localePrefix}.pending_message`)}
              </p>
            </>
          ) : (
            <>
              <div className="w-24 h-24 mb-8 rounded-full bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center text-red-500">
                <VerificationIcon icon="warning" />
              </div>

              <h1 className="text-2xl font-bold text-gray-900 text-center mb-3">
                {t(`${localePrefix}.verify_error_title`)}
              </h1>

              <p className="text-gray-600 text-center text-lg max-w-xs">
                {t(`${localePrefix}.verify_error_message`)}
              </p>
            </>
          )}
        </div>
      )}
    </AppWrapper>
  );
}
